import { Archive, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LearningChatComposer } from "./LearningChatComposer.js";
import type { ChatSession } from "./learning-chat-mock.js";
import { chatSituations, situationToneClass, type ChatSituationId } from "./learning-chat-situations.js";

type LearningChatHomeProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onPrefillDemo: () => void;
  onOpenHistory: (session: ChatSession) => void;
  onOpenSituation: (id: ChatSituationId) => void;
  history: ChatSession[];
  formatDate: (dateKey: string) => string;
};

export function LearningChatHome({
  draft,
  onDraftChange,
  onSend,
  onPrefillDemo,
  onOpenHistory,
  onOpenSituation,
  history,
  formatDate,
}: LearningChatHomeProps) {
  const { t } = useTranslation();
  const hasHistory = history.length > 0;

  return (
    // Intentional (accepted): same full-bleed chat shell as LearningChatSession — mobile
    // bottom clearance may sit under the fixed nav; page-reference overlap is OK in demo.
    <div className="-mx-4 -mb-28 flex min-h-[calc(100dvh-3.5rem)] md:-mx-6 md:mb-0 lg:-mx-10 xl:-mx-12">
      {hasHistory ? (
        <aside className="hidden w-72 shrink-0 flex-col border-r border-border/70 bg-background md:flex lg:w-80">
          {/* Intentional (accepted): pr-28 only clears the overlaid page-reference on small screens. */}
          <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-3 pr-28 md:pr-4">
            <h2 className="text-base font-medium">{t("learningRoute.chatOverview")}</h2>
            <button
              className="rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
              data-control-id="learning-chat.new"
              onClick={onPrefillDemo}
              type="button"
            >
              {t("learningRoute.chatNew")}
            </button>
          </div>
          <div className="px-4 pb-3">
            <label className="flex items-center gap-2 rounded-xl border border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <Search aria-hidden="true" className="size-4 shrink-0" />
              <span className="sr-only">{t("learningRoute.chatSearch")}</span>
              <input
                className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-disabled"
                data-control-id="learning-chat.search"
                placeholder={t("learningRoute.chatSearch")}
                readOnly
              />
            </label>
          </div>
          <ul className="flex-1 space-y-1 overflow-y-auto px-2 pb-4">
            {history.map((session) => (
              <li key={session.id}>
                <button
                  className="w-full rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                  data-control-id={`learning-chat.history.${session.id}`}
                  onClick={() => onOpenHistory(session)}
                  type="button"
                >
                  <span className="line-clamp-2 text-sm font-medium">{session.title}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {formatDate(session.dateKey)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="flex items-center gap-2 border-t border-border/70 px-4 py-3 text-sm text-muted-foreground">
            <Archive aria-hidden="true" className="size-4" />
            {t("learningRoute.chatArchived")}
          </p>
        </aside>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col justify-center px-4 py-8 sm:px-8">
        <div className="mx-auto w-full max-w-2xl text-center">
          <h1 className="text-balance text-3xl font-medium tracking-tight sm:text-4xl">
            {t("learningRoute.chatHeadline")}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("learningRoute.chatSubtitle")}
          </p>
        </div>
        <LearningChatComposer
          className="mx-auto mt-8 w-full max-w-2xl"
          draft={draft}
          onDraftChange={onDraftChange}
          onSend={onSend}
          plusIcon
        />
        <p className="mx-auto mt-3 max-w-2xl text-center text-xs leading-relaxed text-muted-foreground">
          {t("learningRoute.chatPrivacy")}
        </p>
        <section className="mx-auto mt-10 w-full max-w-3xl" aria-labelledby="chat-situations-heading">
          <h2 className="mb-4 text-center text-sm text-muted-foreground" id="chat-situations-heading">
            {t("learningRoute.chatBrowseSituations")}
          </h2>
          <ul className="flex flex-wrap justify-center gap-3">
            {chatSituations.map((situation) => {
              const Icon = situation.icon;
              return (
                <li
                  className="w-full sm:w-[calc(50%-0.375rem)] lg:w-[calc(33.333%-0.5rem)]"
                  key={situation.id}
                >
                  <button
                    className="flex h-full w-full flex-col items-start gap-3 rounded-2xl border border-border bg-background p-4 text-left shadow-xs transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                    data-control-id={`learning-chat.situation.${situation.id}`}
                    onClick={() => onOpenSituation(situation.id)}
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className={`grid size-9 place-items-center rounded-full ${situationToneClass[situation.tone]}`}
                    >
                      <Icon className="size-4" strokeWidth={1.8} />
                    </span>
                    <span className="text-sm font-medium">
                      {t(`learningRoute.situations.${situation.id}.title`)}
                    </span>
                    <span className="text-sm leading-relaxed text-muted-foreground">
                      {t(`learningRoute.situations.${situation.id}.description`)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}
